import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { mapsQueryOf, type Opportunity } from '../../shared/opportunity';
import { TILE, buildFrame, project, useFrameSize, type Size } from '../explore/mapFrame';
import type { DayRoute, OffRouteReason, RouteStop } from './dayRoute';
import { House, PinMark } from './RoutePin';

/**
 * MAP MY DAY's map: the day's stops in order, joined by the route.
 *
 * The callout's map generalised from one event and the one before it to a
 * list of stops, on the same frame (`mapFrame`). It never pans or zooms: it is
 * fitted to the stops. A click on the map opens the day's route in Google
 * Maps; a pin opens its event in the callout, and the house the hotel.
 *
 * A hovered card is put into the route at its time: its pin full size and
 * larger, the legs into and out of it drawn, everything else faded. The frame
 * holds still while the hovered pin is already in it, and glides out to take it
 * in only when it is not (decision 4), so running the pointer down the list
 * moves the map for a far-off event and for nothing else.
 */

/** The palette's red: pins, and a leg that cannot be made. */
const PIN = '#ef3d23';
/** The route: a pale band under a maroon dash, per the reference. */
const ROUTE_UNDER = '#f03d22';
const ROUTE_DASH = '#661a0f';
/** A pin head's height, and the tail under it: the head spans this far above the place. */
const HEAD_H = 20;
const TAIL_H = 6;
/** A head's width for its label: a circle for one number, a pill for "3·4". */
const headWidth = (label: string) => Math.max(HEAD_H, 10 + 6.2 * label.length);
/** The hovered pin's size, and the others' opacity while it is shown. */
const HOVER_SCALE = 1.4;
const FADED = 0.4;
/** How long the frame takes to glide out to a hovered pin outside it. */
const REFIT_MS = 220;
/** A hovered pin nearer the edge than this, or than its enlarged head, is outside the frame. */
const IN_VIEW = { side: 24, top: 44, bottom: 10 };

type Point = [number, number];
/**
 * The places a frame is fitted to, in any order: a hovered event between two
 * at one venue splits their pin in two, but adds no place.
 */
const keyOf = (points: Point[]) => [...new Set(points.map((p) => p.join(',')))].sort().join('|');

/** Where a place falls in a frame, in its pixels. */
const placeIn = (frame: { zoom: number; originX: number; originY: number }, [lat, lng]: Point) => {
  const p = project(lat, lng, frame.zoom);
  return { left: p.x - frame.originX, top: p.y - frame.originY };
};

const Frame = styled('div')({
  position: 'relative',
  width: '100%',
  // The reference's 274 × 305.
  aspectRatio: '274 / 305',
  borderRadius: 4,
  overflow: 'hidden',
  background: '#d9d5cd',
});

const Tile = styled('img')({
  position: 'absolute',
  width: TILE,
  height: TILE,
  userSelect: 'none',
  pointerEvents: 'none',
});

/**
 * The tiles and the route, moved as one when the frame glides. Over the link,
 * but clicks go through it, except on a red leg, which says why on hover.
 */
const Layer = styled('div')({
  position: 'absolute',
  inset: 0,
  zIndex: 2,
  pointerEvents: 'none',
  transformOrigin: '0 0',
});

/** The whole map, as a link: the day's route in Google Maps. Under the pins, which are their own. */
const MapLink = styled('a')({
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  cursor: 'pointer',
});

/** No `viewBox`, as in EventMap: a user unit is a CSS pixel, the space the tiles are placed in. */
const Overlay = styled('svg')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
  overflow: 'hidden',
});

/** A pin: its head, and the point under the tail is the place. */
const PinButton = styled('button', { shouldForwardProp: (p) => p !== '$hovered' && p !== '$faded' })<{
  $hovered: boolean;
  $faded: boolean;
}>(({ $hovered, $faded }) => ({
  appearance: 'none',
  position: 'absolute',
  // Scaled about the tail's tip, which stays on the place.
  transform: `translate(-50%, -100%)${$hovered ? ` scale(${HOVER_SCALE})` : ''}`,
  transformOrigin: '50% 100%',
  opacity: $faded ? FADED : 1,
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.45))',
  zIndex: $hovered ? 4 : 3,
  '&:hover, &:focus-visible': { zIndex: 4 },
  '&:focus-visible': { outline: `2px solid ${BRAND.mocha}`, outlineOffset: 2, borderRadius: 11 },
  // Moving with the tiles while the frame glides.
  '[data-gliding] > &': { transition: `left ${REFIT_MS}ms ease, top ${REFIT_MS}ms ease` },
}));

const Below = styled('div')({
  marginTop: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
});

/** An event that is on the day but not on the route, and why. */
const OffRoute = styled('button')({
  appearance: 'none',
  display: 'flex',
  alignItems: 'baseline',
  gap: 8,
  padding: 0,
  border: 'none',
  background: 'none',
  textAlign: 'left',
  cursor: 'pointer',
  color: BRAND.parchment,
  fontFamily: FONTS.body,
  fontSize: 13,
  lineHeight: 1.35,
  '& > span:first-of-type': {
    flexShrink: 0,
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'rgba(243,238,231,0.55)',
  },
  '&:hover > span:last-of-type': { textDecoration: 'underline', textUnderlineOffset: 3 },
  // The hovered card, when it is one of these.
  '&[data-preview] > span:last-of-type': { fontWeight: 700 },
});

const Credit = styled('div')({
  marginTop: 8,
  fontFamily: FONTS.body,
  fontSize: 10.5,
  lineHeight: 1.45,
  color: 'rgba(243,238,231,0.42)',
  '& a': { color: 'inherit', textUnderlineOffset: 2 },
});

const WHY: Record<OffRouteReason, string> = {
  online: 'Online',
  untimed: 'No set time',
  unplaced: 'No location yet',
};


/** What a pin says on hover: every event at it, and what it clashes with. */
function pinTitle(stop: RouteStop, homeLabel: string | undefined): string {
  if (stop.home) return `${homeLabel ?? 'Your hotel'}: where the day starts and ends. Click to change it.`;
  const lines = stop.events.map(({ event, number }) => `${number}. ${event.name}`);
  const here = new Set(stop.events.map((e) => e.event.id));
  const elsewhere = stop.clashesWith.filter((e) => !here.has(e.id));
  if (stop.clashesWith.length) {
    lines.push(
      elsewhere.length
        ? `Runs at the same time as ${elsewhere.map((e) => e.name).join(' and ')}`
        : 'These run at the same time',
    );
  }
  if (stop.approximate) lines.push('No street address published: the pin is the neighborhood.');
  return lines.join('\n');
}

/**
 * Google Maps directions through every stop with a real place, in order; for
 * a day with one place, that place.
 */
function mapUrl(stops: RouteStop[]): string | null {
  const route = directionsUrl(stops);
  if (route) return route;
  const only = stops.find((s) => !s.home);
  if (!only) return null;
  const query = mapsQueryOf(only.events[0].event) ?? only.point.join(',');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function directionsUrl(stops: RouteStop[]): string | null {
  const places = stops
    .filter((s) => !s.approximate)
    .map((s) => (s.home ? s.point.join(',') : (mapsQueryOf(s.events[0].event) ?? s.point.join(','))));
  // The hotel twice in a row, when nothing between them has an address, is no journey.
  const route = places.filter((p, i) => p !== places[i - 1]);
  if (route.length < 2) return null;
  const [origin, ...rest] = route;
  const destination = rest.pop() as string;
  const waypoints = rest.slice(0, 9).join('|');
  return (
    'https://www.google.com/maps/dir/?api=1' +
    `&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}` +
    (waypoints ? `&waypoints=${encodeURIComponent(waypoints)}` : '')
  );
}

interface Props {
  route: DayRoute;
  /** The hovered card, when it is in the route: drawn out, and the rest faded. */
  previewId?: string;
  homeLabel?: string;
  onOpenEvent: (event: Opportunity) => void;
  /** The house: set, change or forget the hotel. */
  onEditHome: () => void;
}

/** Is a place inside a frame of this size, with room for the hovered pin's head? */
function inView(frame: { zoom: number; originX: number; originY: number }, point: Point, size: Size) {
  const { left, top } = placeIn(frame, point);
  return (
    left >= IN_VIEW.side &&
    left <= size.width - IN_VIEW.side &&
    top >= IN_VIEW.top &&
    top <= size.height - IN_VIEW.bottom
  );
}

export default function DayRouteMap({ route, previewId, homeLabel, onOpenEvent, onEditHome }: Props) {
  const [frameRef, size] = useFrameSize();
  const { stops, legs, offRoute } = route;

  const hovered = previewId ? stops.findIndex((s) => s.events.some((e) => e.event.id === previewId)) : -1;
  // A hovered event at a place already on the route adds no new place to fit.
  const alone = hovered >= 0 && stops[hovered].events.every((e) => e.event.id === previewId);
  const committed = useMemo(
    () => stops.filter((_, i) => !(alone && i === hovered)).map((s) => s.point),
    [stops, alone, hovered],
  );
  const previewPoint = alone ? stops[hovered].point : null;
  const committedKey = keyOf(committed);

  /**
   * What the frame is fitted to: the day's own stops, and a hovered one only
   * once it fell outside them. Kept between hovers, so the next event inside
   * the wider frame does not pull it back; the pointer leaving does.
   */
  const [fit, setFit] = useState({ base: committedKey, points: committed, glide: false });
  let next = fit;
  // Another day, or a changed schedule: a new map, placed rather than glided to.
  if (fit.base !== committedKey) next = { base: committedKey, points: committed, glide: false };
  if (previewPoint) {
    const widened = [...committed, previewPoint];
    const shown = next.points.length ? buildFrame(next.points, size).frame : null;
    // Already fitted to it: refit only once. A frame fitted to the pin can still
    // hold it inside `IN_VIEW`'s margins (they are wider than `fitZoom`'s), and
    // a fresh `next` on every render would set state on every render.
    if (keyOf(next.points) !== keyOf(widened) && (!shown || !inView(shown, previewPoint, size))) {
      next = { base: committedKey, points: widened, glide: next === fit };
    }
  } else if (!previewId && keyOf(next.points) !== committedKey) {
    // Only once no card is hovered: one at a place already shown, or online, keeps the frame too.
    next = { base: committedKey, points: committed, glide: true };
  }
  if (next !== fit) setFit(next);

  const frame = useMemo(() => (next.points.length ? buildFrame(next.points, size).frame : null), [next.points, size]);
  const placed = useMemo(() => (frame ? stops.map((s) => placeIn(frame, s.point)) : []), [frame, stops]);

  /**
   * The glide: the new frame is drawn at once, then shown as the old one and
   * eased into place, tiles and route together; the pins ease the same way.
   */
  const layer = useRef<HTMLDivElement | null>(null);
  const shownView = useRef<{ fit: typeof fit; zoom: number; x: number; y: number } | null>(null);
  useLayoutEffect(() => {
    const el = layer.current;
    const prev = shownView.current;
    if (!frame) {
      shownView.current = null;
      return;
    }
    shownView.current = { fit: next, zoom: frame.zoom, x: frame.originX, y: frame.originY };
    if (!el || !prev || prev.fit === next || !next.glide) return;
    if (prev.zoom === frame.zoom && prev.x === frame.originX && prev.y === frame.originY) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const host = el.parentElement;
    host?.setAttribute('data-gliding', '');
    const k = 2 ** (prev.zoom - frame.zoom);
    el.style.transition = 'none';
    el.style.transform = `translate(${k * frame.originX - prev.x}px, ${k * frame.originY - prev.y}px) scale(${k})`;
    el.getBoundingClientRect();
    el.style.transition = `transform ${REFIT_MS}ms ease`;
    el.style.transform = '';
    const done = window.setTimeout(() => host?.removeAttribute('data-gliding'), REFIT_MS + 40);
    return () => window.clearTimeout(done);
  }, [frame, next]);

  /**
   * Where each pin is drawn: at its place, unless its head would cover one
   * already drawn, when it steps sideways until both numbers read. Two venues
   * a street apart are one blot at the zoom that fits the Bronx.
   */
  const pins = useMemo(() => {
    const drawn: { l: number; r: number; t: number; b: number }[] = [];
    return stops.map((stop, i) => {
      // The hotel is one pin, however many times the route passes it.
      if (stop.home && i > 0) return null;
      const at = placed[i];
      if (!at) return null;
      // A clash's ring stands 4px proud of the head.
      const ring = stop.clashesWith.length ? 4 : 0;
      const half = headWidth(stop.home ? '' : stop.events.map((e) => e.number).join('·')) / 2 + ring;
      const t = at.top - TAIL_H - HEAD_H - ring;
      const b = at.top - TAIL_H + ring;
      let left = at.left;
      for (let guard = 0; guard < 12; guard += 1) {
        const hit = drawn.find((d) => left - half < d.r + 2 && left + half > d.l - 2 && t < d.b && b > d.t);
        if (!hit) break;
        // Out to whichever side of it is nearer.
        left = left >= (hit.l + hit.r) / 2 ? hit.r + 2 + half : hit.l - 2 - half;
      }
      drawn.push({ l: left - half, r: left + half, t, b });
      return { left, top: at.top };
    });
  }, [placed, stops]);

  const link = mapUrl(stops);

  return (
    <div>
      {frame && (
        <Frame ref={frameRef} role="group" aria-label="Your route for the day">
          {link && (
            <MapLink
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open the day's route in Google Maps"
            />
          )}
          <Layer ref={layer}>
          {frame.tiles.map((t) => (
            <Tile key={t.key} src={t.url} alt="" loading="lazy" style={{ left: t.left, top: t.top }} />
          ))}

          <Overlay aria-hidden="true">
            {legs.map((leg) => {
              const a = placed[leg.from];
              const b = placed[leg.to];
              const clash = leg.clashes.length > 0;
              const faded = hovered >= 0 && leg.from !== hovered && leg.to !== hovered;
              return (
                <g key={`${leg.from}-${leg.to}`} opacity={faded ? FADED : 1}>
                  <line x1={a.left} y1={a.top} x2={b.left} y2={b.top} stroke={ROUTE_UNDER} strokeOpacity={0.2} strokeWidth={6} strokeLinecap="round" />
                  <line
                    x1={a.left}
                    y1={a.top}
                    x2={b.left}
                    y2={b.top}
                    stroke={clash ? PIN : ROUTE_DASH}
                    strokeWidth={2.5}
                    strokeDasharray="6 5"
                    strokeLinecap="round"
                  />
                  {clash && (
                    // A wide, invisible stroke to hover: this leg can't be made, and why.
                    <line x1={a.left} y1={a.top} x2={b.left} y2={b.top} stroke="transparent" strokeWidth={12} pointerEvents="stroke">
                      <title>
                        {leg.clashes.map(([x, y]) => `${x.name} and ${y.name} run at the same time.`).join('\n') +
                          "\nYou can't make this leg."}
                      </title>
                    </line>
                  )}
                </g>
              );
            })}
          </Overlay>
          </Layer>

          {stops.map((stop, i) => {
            const pin = pins[i];
            if (!pin) return null;
            const label = stop.home ? <House /> : stop.events.map((e) => e.number).join('·');
            return (
              <PinButton
                key={stop.key}
                type="button"
                $hovered={i === hovered}
                $faded={hovered >= 0 && i !== hovered}
                style={{ left: pin.left, top: pin.top }}
                title={pinTitle(stop, homeLabel)}
                aria-label={pinTitle(stop, homeLabel)}
                onClick={() => (stop.home ? onEditHome() : onOpenEvent(stop.events[0].event))}
              >
                <PinMark label={label} hollow={stop.approximate} clash={stop.clashesWith.length > 0} />
              </PinButton>
            );
          })}
        </Frame>
      )}

      {offRoute.length > 0 && (
        <Below>
          {offRoute.map(({ event, why }) => (
            <OffRoute
              key={event.id}
              type="button"
              data-preview={event.id === previewId ? '' : undefined}
              onClick={() => onOpenEvent(event)}
            >
              <span>{WHY[why]}</span>
              <span>{event.name}</span>
            </OffRoute>
          ))}
        </Below>
      )}

      {frame && (
        <Credit>
          {link && 'Click the map for directions. '}
          {stops.some((s) => s.approximate) && 'Hollow pins: no street address, so the neighborhood. '}
          Tiles by Stamen / Stadia Maps, data © OpenStreetMap.
        </Credit>
      )}
    </div>
  );
}
